import { SubstackFeedEntry } from "@/types/import"
import { parse } from "csv-parse/sync"
import { unzip } from "unzipit"

/**
 * The type in posts.csv, listing all the posts.
 *
 * This does NOT contain the text of the post - those are extracted separately.
 */
type PostsType = {
  post_date: string
  is_published: boolean
  post_id: string
  title: string
  subtitle: string
}

function isCustomDomain(url: string): boolean {
  return !url.includes("substack.com")
}

function getHostFromUrl(url: string): string {
  // Add protocol if missing
  if (!url.match(/^[a-zA-Z]+:\/\//)) {
    url = "http://" + url
  }

  try {
    const urlObj = new URL(url)
    return urlObj.host
  } catch (e) {
    console.error("Error getting host from URL " + url, e)
    return "" // or return a default value, or handle as appropriate
  }
}

/**
 * Substack stores post IDs in their CSV in the form of:
 *
 * <some_number>.<post-slug>
 *
 * eg 1234567123123.hidden-gems-extracted
 *
 * So we need to extract the slug from the post ID directly and return that here,
 * for use in redirects.
 *
 * However, we only want to do this if the user is importing from a custom domain.
 *
 */
function getLink(url: string, postId: string): string | undefined {
  try {
    console.log("Beginning to get link", url, postId)

    if (!isCustomDomain(url)) {
      return undefined
    }

    const customDomainUrl = getHostFromUrl(url)
    const postSlug = postId.split(".")[1]

    if (!postSlug || !customDomainUrl) return undefined

    return `${customDomainUrl}/p/${postSlug}`
  } catch (e) {
    console.error(e)
    return undefined
  }
}

/**
 * Takes in a Substack export file and returns an array of FeedEntry objects.
 *
 * The export contains a 'posts.csv' which lists all the posts,
 * and a 'posts' directory containing the HTML of each post.
 */
export async function importFromFile(
  data: File,
  url: string
): Promise<Array<SubstackFeedEntry>> {
  try {
    console.log(
      "Starting Substack import processing",
      data.name,
      data.type,
      data.size
    )

    // Ensure we have valid data
    if (data.size === 0) {
      throw "The uploaded file is empty."
    }

    // Log available properties and methods on the File object
    console.log("File properties:", {
      name: data.name,
      size: data.size,
      type: data.type,
      lastModified: data.lastModified,
    })

    // Unzip the file content
    const feed = await unzip(data)
    const entryCount = feed?.entries ? Object.keys(feed.entries).length : 0
    console.log(`Unzipped file successfully, found ${entryCount} entries.`)

    if (entryCount === 0) {
      throw "No files found in the ZIP archive."
    }

    // Log the first few entries to debug
    const entryKeys = Object.keys(feed.entries)
    console.log("First 5 entries:", entryKeys.slice(0, 5))

    const entries = feed?.entries

    if (!entries || Object.keys(entries).length === 0) {
      throw "Could not extract any files from the ZIP archive. The file may be corrupted or not a valid Substack export."
    }

    const postData = entries?.["posts.csv"]
    if (!postData) {
      throw "Could not find posts.csv in the ZIP archive. This doesn't appear to be a valid Substack export."
    }

    const bytes = await postData?.arrayBuffer()
    if (!bytes) {
      throw "Error reading posts.csv from export -- is the file an incorrect format?"
    }

    const parsed: Array<PostsType> = parse(Buffer.from(bytes), {
      columns: true,
    })

    // Filter for only published posts
    const publishedPosts = parsed.filter((post) => post.is_published)
    console.log(
      `Found ${publishedPosts.length} published posts out of ${parsed.length} total`
    )

    if (publishedPosts.length === 0) {
      throw "No published posts found in the Substack export."
    }

    // Map posts to the expected format
    const posts = await Promise.all(
      publishedPosts.map(async (post) => {
        try {
          // Get HTML file path
          const htmlFileName = `posts/${post.post_id}.html`
          console.log(
            `Looking for HTML file: ${htmlFileName} for post: ${post.title}`
          )

          const postHtmlBytes = entries?.[htmlFileName]
          if (!postHtmlBytes) {
            console.warn(
              `HTML file not found for post: ${post.title}, id: ${post.post_id}`
            )
            return null
          }

          // Extract content
          const content = await postHtmlBytes.text()
          if (!content || content.trim().length === 0) {
            console.warn(`Empty HTML content for post: ${post.title}`)
            return null
          }

          // Log the content for debugging
          console.log(
            `Successfully extracted HTML for post: ${post.title}, length: ${content.length}`
          )
          console.log(
            `HTML content sample (first 150 chars): "${content.substring(0, 150).replace(/\n/g, " ")}..."`
          )

          // Create the entry with explicit content field
          const entry: SubstackFeedEntry = {
            title: post.title,
            subtitle: post.subtitle || "",
            published: new Date(post.post_date),
            content: content, // This is the critical field
            link: getLink(url, post.post_id),
          }

          console.log(
            `Created entry for ${post.title} with content length: ${entry.content?.length || 0}`
          )
          return entry
        } catch (err) {
          console.error(
            `Error processing post ${post.post_id}: ${post.title}`,
            err
          )
          return null
        }
      })
    )

    // Remove any null entries (posts that couldn't be processed)
    const validPosts = posts.filter(
      (post) => post !== null
    ) as SubstackFeedEntry[]

    console.log(
      `After filtering, returning ${validPosts.length} valid posts out of ${posts.length} total`
    )

    if (validPosts.length === 0) {
      throw "No valid posts could be extracted from the Substack export."
    }

    return validPosts
  } catch (error) {
    console.error("Error processing Substack export:", error)
    throw typeof error === "string"
      ? error
      : "Failed to process Substack export. The file may be invalid or corrupted."
  }
}
